<template>
<card>
    <template slot="header">
        <div style="float: right">
            <base-button v-if="loggedUser.role === UserRoles.ADMIN" type="primary" size="lg" class="btn-fill mr-2" v-on:click="downloadEvaluations">
                Download evaluations
            </base-button>
            <base-button v-if="loggedUser.role === UserRoles.ADMIN || loggedUser.role === UserRoles.REGIONAL_ADMIN" type="warning" size="lg" class="btn-fill mr-2" v-on:click="exportFinalRanking">
                Export ranking
            </base-button>
            <base-button v-if="loggedUser.role === UserRoles.ADMIN || loggedUser.role === UserRoles.REGIONAL_ADMIN" type="warning" size="lg" class="btn-fill" v-on:click="exportList">
                Export list
            </base-button>
        </div>
        <h2 class="card-title">{{$t('documents.list')}}</h2>
    </template>
    <div class="row">
        <div class="col-12">
            <card card-body-classes="table-full-width">
                <div>
                    <tabs ref="tab-calls" type="info" @activate-tab="activateTab" square>
                        <tab-pane :id="call.name" :data-id="call.id" :label="call.title" v-for="call in calls" v-bind:key="call.id">
                            <div class="col-12 d-flex justify-content-center justify-content-sm-between flex-wrap">
                                <div>
                                    <el-select class="filter-select mr-2" v-if="loggedUser.role === UserRoles.ADMIN" v-model="filters.regionId" placeholder="Filter by region" clearable @change="handleFilters">
                                        <el-option v-for="item in filterOptions.regions" :key="item.value" :label="item.text + ' ('+item.total+')'" :value="item.value" />
                                    </el-select>
                                    <el-select class="filter-select mr-2" v-model="filters.applicationStatus" placeholder="Filter by status" clearable @change="handleFilters">
                                        <el-option v-for="item in filterOptions.application_statuses" :key="item.value" :label="item.text + ' ('+item.total+')'" :value="item.value" />
                                    </el-select>
                                    <el-select class="filter-select mr-2" v-model="filters.evaluationStatus" placeholder="Filter by evaluation status" clearable @change="handleFilters">
                                        <el-option v-for="item in filterOptions.evaluation_statuses" :key="item.value" :label="item.text + ' ('+item.total+')'" :value="item.value" />
                                    </el-select>
                                    <el-select class="filter-select mr-2" v-model="filters.objectives" multiple collapse-tags placeholder="Filter by objectives" clearable @change="handleFilters">
                                        <el-option v-for="item in filterOptions.objectives" :key="item.value" :label="$t(item.text) + ' ('+item.total+')'" :value="item.value" />
                                    </el-select>
                                </div>
                                <div class="subsection-switch-container">
                                    <p class="category" style="font-size: 16px; margin-bottom: 10px;">Concept Note / Full Proposal</p>
                                    <base-switch v-model="isCN" type="info" @input="handleFilters" on-text="CN" off-text="FP" style="transform: scale(1.4) translate(10px, 3px)"></base-switch>
                                </div>
                            </div>

                            <el-table :data="queriedData" style="table-layout: auto">
                                <el-table-column width="110" label="Reference" property="reference_nr" prop="reference_nr"></el-table-column>
                                <el-table-column width="190" label="Organization" property="fp_organization" prop="fp_organization" v-if="!isCN"></el-table-column>
                                <el-table-column width="190" label="Organization" property="organization" prop="organization" v-else></el-table-column>

                                <el-table-column label="ESMS Risk" width="80" property="esms_preliminary_check" prop="esms_preliminary_check" v-if="isCN">
                                    <div slot-scope="props" class="align-center">
                                        <span class="badge">{{props.row.esms_preliminary_check}}</span>
                                    </div>
                                </el-table-column>
                                <el-table-column width="140" label="FP Application Status" property="stage_two_status" prop="stage_two_status" v-else>
                                    <div slot-scope="props" class="align-center">
                                        <span v-if="props.row.stage_two_status === 'SUBMITTED'" class="badge badge-success">{{props.row.stage_two_status}}</span>
                                        <span v-else class="badge badge-danger">{{props.row.stage_two_status}}</span>
                                    </div>
                                </el-table-column>

                                <el-table-column width="130" label="Admissibility Eligibility Check" property="admissibility_eligibility_check" prop="admissibility_eligibility_check" v-if="isCN">
                                    <div slot-scope="props" class="align-center">
                                        <span class="badge">{{props.row.admissibility_eligibility_check}}</span>
                                    </div>
                                </el-table-column>
                                <el-table-column width="130" label="Admissibility Eligibility Check" property="admissibility_eligibility_check" prop="admissibility_eligibility_check" v-else>
                                    <div slot-scope="props" class="align-center">
                                        <span class="badge">{{props.row.fp_admissibility_eligibility_check}}</span>
                                    </div>
                                </el-table-column>

                                <el-table-column width="120" label="Budget Review" property="budget_review" prop="budget_review" v-if="!isCN">
                                    <div slot-scope="props" class="align-center">
                                        <span class="badge">{{props.row.budget_review}}</span>
                                    </div>
                                </el-table-column>

                                <el-table-column width="140" label="ESMS Screening" property="esms_screening" prop="esms_screening" v-if="!isCN">
                                    <div slot-scope="props" class="align-center">
                                        <span class="badge" style="word-break: break-word; white-space: normal;">{{props.row.esms_screening}}</span>
                                    </div>
                                </el-table-column>

                                <el-table-column width="130" label="ESMS Clearance" property="esms_clearance" prop="esms_clearance" v-if="!isCN">
                                    <div slot-scope="props" class="align-center">
                                        <span class="badge" style="word-break: break-word; white-space: normal;">{{props.row.esms_clearance}}</span>
                                    </div>
                                </el-table-column>

                                <el-table-column width="190" label="CN Application Status" property="application_status" prop="application_status" v-if="isCN">
                                    <div slot-scope="props" class="align-center">
                                        <span v-if="props.row.application_status === 'SUBMITTED'" class="badge badge-success">{{props.row.application_status}}</span>
                                        <span v-else-if="props.row.application_status === 'DRAFT'" class="badge badge-light">{{props.row.application_status}}</span>
                                        <span v-else class="badge badge-danger">{{props.row.application_status}}</span>
                                    </div>
                                </el-table-column>

                                <el-table-column label="Due Dilligence" width="125" property="due_diligence_check" prop="due_diligence_check" v-if="isCN">
                                    <div slot-scope="props" class="align-center">
                                        <span class="badge">{{props.row.due_diligence_check}}</span>
                                    </div>
                                </el-table-column>


                                <el-table-column width="120" label="CN Evaluation" property="evaluation_status" prop="evaluation_status" v-if="isCN">
                                    <div slot-scope="props">
                                        <span v-if="props.row.evaluation_status === 'Rejected' || props.row.evaluation_status === 'Non-eligible'" class="badge badge-danger">{{props.row.evaluation_status}}</span>
                                        <span v-else class="badge badge-success">{{props.row.evaluation_status}}</span>
                                    </div>
                                </el-table-column>
                                <el-table-column width="130" label="FP Evaluation" property="fp_evaluation_status" prop="fp_evaluation_status" v-else>
                                    <div slot-scope="props">
                                        <span v-if="props.row.fp_evaluation_status === 'Rejected' || props.row.fp_evaluation_status === 'Non-eligible'" class="badge badge-danger">{{props.row.fp_evaluation_status}}</span>
                                        <span v-else class="badge badge-success">{{props.row.fp_evaluation_status}}</span>
                                    </div>
                                </el-table-column>

                                <el-table-column width="120" label="Final Status" property="final_status" prop="final_status" v-if="!isCN">
                                    <div slot-scope="props">
                                        <span v-if="props.row.final_status === 'Rejected'" class="badge badge-danger">{{props.row.final_status}}</span>
                                        <span v-else class="badge badge-success">{{props.row.final_status}}</span>
                                    </div>
                                </el-table-column>


                                <el-table-column align="right" label="View Proposal / Evaluation">
                                    <div slot-scope="props">
                                        <div>
                                            <base-button type="info" size="sm" class="mb-1" @click="view(props.row)">
                                                View Documents
                                            </base-button>
                                        </div>
                                        <div>
                                            <base-button class="mb-1" type="success" size="sm" v-if="loggedUser.role === UserRoles.ADMIN" @click="requestClarifications(props.row)">
                                                <i class="tim-icons icon-alert-circle-exc"></i> Request Clarifications
                                            </base-button>
                                        </div>
                                        <div>
                                            <base-button class="mb-1" :type="props.row.has_revision ? 'green' : 'orange'" size="sm" v-if="!isCN && loggedUser.role === UserRoles.ADMIN" @click="requestRevision(props.row)">
                                                <i class="tim-icons icon-zoom-split"></i> {{ (props.row.has_revision ? 'Open for Revision' : 'Request Revision') }}
                                            </base-button>
                                        </div>
                                        <div v-if="!isCN || loggedUser.role !== UserRoles.APPLICANT">
                                            <div v-if="!isCN">
                                                <div v-if="loggedUser.role !== UserRoles.EVALUATOR">
                                                    <div>
                                                        <base-button class="mb-1" type="info" size="sm" @click="changeFinalStatusModal(props.row)">
                                                            <i class="tim-icons icon-paper"></i> Change Final Status
                                                        </base-button>
                                                    </div>
                                                </div>
                                            </div>
                                            <div class="btn-group dropdown" v-if="!isCN">
                                                <base-button class="mb-1" type="warning" size="sm" @click="evaluateApplicationModal(props.row)">
                                                    <i class="tim-icons icon-paper"></i> Evaluate
                                                </base-button>
                                                <base-button class="mb-1" type="warning" size="sm" data-toggle="dropdown">
                                                    <i class="tim-icons icon-cloud-download-93"></i>
                                                </base-button>
                                                <ul class="dropdown-menu">
                                                    <li><a @click="exportEvaluation(props.row, 'all')">Export All</a></li>
                                                    <li v-for="applicationEvaluation in props.row.evaluations" :key="applicationEvaluation.id">
                                                        <a @click="exportEvaluation(props.row, evaluationTypes.find(function (i) { return i.evaluation_type_id === applicationEvaluation.evaluation_type_id }).nickname)">{{ (evaluationTypes.find(function (i) { return i.evaluation_type_id === applicationEvaluation.evaluation_type_id }) || {name: ''}).name }}</a>
                                                    </li>
                                                </ul>
                                            </div>
                                            <div v-else>
                                                <div>
                                                    <base-button class="mb-1" type="warning" size="sm" @click="evaluateApplicationModal(props.row)">
                                                        <i class="tim-icons icon-paper"></i> Evaluate
                                                    </base-button>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </el-table-column>
                            </el-table>
                            <div slot="footer" class="col-12 d-flex justify-content-center justify-content-sm-between flex-wrap">
                                <div class="">
                                    <p class="card-category">
                                        Showing {{ from + 1 }} to {{ to }} of {{ total }} entries
                                    </p>
                                </div>
                                <base-pagination class="pagination-no-border" v-model="pagination.currentPage" :per-page="pagination.perPage" :total="total">
                                </base-pagination>
                            </div>
                        </tab-pane>
                    </tabs>
                </div>
            </card>
        </div>
    </div>

    <modal :show.sync="modalEvaluate.visibility" headerClasses="justify-content-center">
        <h4 slot="header" class="title">Evaluate documents: <strong>{{modalEvaluate.documents.reference_nr}}</strong></h4>
        <ol>
            <li v-for="evaluationType in evaluationTypes" :key="evaluationType.id">
                <a @click="evaluateApplication(modalEvaluate.documents, evaluationType)" class="pl-1 link-text"> {{evaluationType.name}} </a>
                <ul class="evaluations-list">
                    <li v-if="evaluation.type.nickname === evaluationType.nickname" v-for="evaluation in modalEvaluate.documents.evaluations" :key="evaluation.id">
                        <span v-if="evaluationType.nickname === 'technical' || evaluationType.nickname === 'fp_technical'">
                            <a @click="evaluateApplication(modalEvaluate.documents, evaluationType, evaluation.evaluator_id)" class="pl-1 link-text"> {{evaluation.status}} </a>
                            ({{evaluation.evaluator.name}})
                        </span> <span v-else>{{evaluation.status}} ({{evaluation.evaluator.name}})</span>
                    </li>
                </ul>
            </li>
        </ol>

        <div class="row">
            <div class="col-8 mt-2" style="width: 290px">
                <template v-if="loggedUser.role === UserRoles.ADMIN">
                    <el-select v-if="isCN" class="filter-select mr-2 mb-2" v-model="modalEvaluate.documents.evaluation_status" placeholder="Choose evaluation status" clearable @change="changeEvaluationStatus">
                        <el-option v-for="item in cnEvaluationStatuses" :key="item" :label="item" :value="item" />
                    </el-select>
                    <el-select v-else class="filter-select mr-2 mb-2" v-model="modalEvaluate.documents.fp_evaluation_status" placeholder="Choose evaluation status" clearable @change="changeEvaluationStatus">
                        <el-option v-for="item in fpEvaluationStatuses" :key="item" :label="item" :value="item" />
                    </el-select>
                    <span>(Updated at: {{modalEvaluate.documents.evaluation_status_updated_at}})</span>
                </template>
            </div>
            <div class="col-4 mt-2 text-right">
                <base-button type="danger" @click.native="modalEvaluate.visibility = false">Close
                </base-button>
            </div>
        </div>
    </modal>

    <modal :show.sync="modalFinalStatus.visibility" headerClasses="justify-content-center">
        <h4 slot="header" class="title">Set final status for: <strong>{{modalFinalStatus.documents.reference_nr}}</strong></h4>
        <div class="row" style="display: flex; justify-content: center">
            <template v-if="loggedUser.role === UserRoles.ADMIN">
                <el-select class="filter-select mr-2 mb-2" v-model="modalFinalStatus.documents.final_status" placeholder="Choose final status" clearable @change="changeFinalStatus">
                    <el-option v-for="item in finalStatuses" :key="item" :label="item" :value="item" />
                </el-select><br />
            </template>
        </div>
        <div class="row" style="display: flex; justify-content: center">
            <span v-if="loggedUser.role === UserRoles.ADMIN">(Updated at: {{modalFinalStatus.documents.final_status_updated_at}})</span>
        </div>

        <div class="row">
            <div class="col-8 mt-2" style="width: 290px">
            </div>
            <div class="col-4 mt-2 text-right">
                <base-button type="danger" @click.native="modalFinalStatus.visibility = false">Close
                </base-button>
            </div>
        </div>
    </modal>

</card>
</template>
<script>
import { Table, TableColumn, Select, Option } from 'element-ui';
import { Modal, TabPane, Tabs, BaseSwitch } from 'src/components';
import { BasePagination } from 'src/components';
import Fuse from 'fuse.js';
import { DocumentService, SummaryService, UserRoles, RegionService, EvaluationService, CallService, ProposalService } from "@/services";


export default {
    components: {
        TabPane,
        Tabs,
        BaseSwitch,
        BasePagination,
        [Select.name]: Select,
        [Option.name]: Option,
        [Table.name]: Table,
        [TableColumn.name]: TableColumn,
        Modal
    },
    computed: {
        /***
         * Returns a page from the searched data or the whole data. Search is performed in the watch section below
         */
        queriedData() {
            let result = this.tableData;
            if (this.searchedData.length > 0) {
                result = this.searchedData;
            }

            // if (!this.isCN)
            // {
            //   result = result.filter((item) => item.evaluation_status === 'Preselected');
            // }

            return result.slice(this.from, this.to);
        },
        to() {
            let highBound = this.from + this.pagination.perPage;
            if (this.total < highBound) {
                highBound = this.total;
            }
            return highBound;
        },
        from() {
            return this.pagination.perPage * (this.pagination.currentPage - 1);
        },
        total() {
            return this.searchedData.length > 0 ?
                this.searchedData.length :
                this.tableData.length;
        },
        documentsGrids() {
            let grids = this.modalEvaluate.documents.grids || [];

            if (this.isCN) {
                return grids.filter((grid) => grid.stage === 1);
            } else {
                return grids.filter((grid) => grid.stage === 2);
            }

            return [];
        }
    },
    data() {
        return {
            pagination: {
                perPage: 10000,
                currentPage: 1,
                perPageOptions: [5, 10, 25, 50],
                total: 0
            },
            isCN: true,
            currentCallId: 1,
            searchQuery: '',
            tableData: [],
            secondTableData: [],
            searchedData: [],
            evaluationTypes: [],
            calls: [],
            fuseSearch: null,
            initialized: false,
            UserRoles,
            loggedUser: {},
            modalEvaluate: {
                visibility: false,
                documents: {}
            },
            modalFinalStatus: {
                visibility: false,
                documents: {}
            },
            regions: [],
            cnEvaluationStatuses: [
                'Rejected',
                'Preselected',
                'Non-eligible'
            ],
            fpEvaluationStatuses: [
                'Rejected',
                'Passed',
                'Non-eligible'
            ],
            finalStatuses: [
                'Rejected',
                'Awarded'
            ],
            filterOptions: [],
            filters: {
                regionId: undefined,
                applicationStatus: undefined,
                evaluationStatus: undefined,
                fpEvaluationStatus: undefined
            },

        };
    },
    methods:
    {
        view(data)
        {
            window.open('/proposals/fill/' + data.call_id + '/' + data.id)
        },
        evaluateApplicationModal(documents) {
            let vm = this;

            vm.modalEvaluate.visibility = true;
            vm.modalEvaluate.documents = documents;
        },
        changeFinalStatusModal(documents) {
            const vm = this;

            vm.modalFinalStatus.visibility = true;
            vm.modalFinalStatus.documents = documents;
        },
        evaluateSummary(documents) {
            if (documents) {
                if (documents.summaries.length === 0) {
                    SummaryService.createSummaryReport({
                        user_id: documents.id
                    }).then(function(response) {
                        window.open('/reports/summary-report/' + response.data.summary_id);
                    });
                } else {
                    window.open('/reports/summary-report/' + documents.summaries[0].summary_id);
                }
            }
        },
        evaluateSummaryById(summaryId) {
            window.open('/reports/summary-report/' + summaryId);
        },
        evaluateSummaryGrid(documents) {
            if (documents) {
                SummaryService.createSummaryGrid({
                    summary_id: documents.summaries[0].id,
                    stage: (this.isCN ? 1 : 2)
                }).then(function(response) {
                    window.open('/reports/evaluation-grid/' + response.data.grid_id);
                });
            }
        },
        evaluateApplication(application, evaluationType, evaluationUserId) {
            const callId = (application.call_id ? application.call_id : 1);

            if (evaluationUserId) {
                window.open('/application/evaluate/' + evaluationType.nickname + '/' + callId + '/' + application.id + '/' + evaluationUserId, '_blank');
            } else {
                window.open('/application/evaluate/' + evaluationType.nickname + '/' + callId + '/' + application.id, '_blank');
            }
        },
        evaluationGridSummary(summaryId) {
            window.open('/reports/evaluation-grid/' + summaryId);
        },
        requestClarifications(data) {
            this.$router.push('/clarifications/fill/' + this.currentCallId + '/' + data.id);
        },
        requestRevision(documents) {
            let vm = this;

            if (documents.has_revision) {
                window.open('/documents/fill/' + documents.id)
            } else {
                if (confirm(vm.$t('documents.request_revision__confirmation'))) {
                    DocumentService.requestRevision({
                        proposal_id: documents.id
                    }).then((response) => {
                        documents.has_revision = true;
                    });
                }
            }

        },
        exportFinalRanking() {
            let vm = this;
            let loader = vm.$loading.show();

            ProposalService.exportFinalRanking({
                call_id: this.currentCallId,
                is_cn: vm.isCN,
                region_id: this.filters.regionId
            }).then(function(response)
            {
                if (response.data && response.data.success)
                {
                    window.open(response.data.url, '_blank');
                }

                loader.hide();
            });
        },
        exportList() {
            let vm = this;
            let loader = vm.$loading.show();

            ProposalService.exportList(vm.filters).then(function (response)
            {
                if (response.data && response.data.success)
                {
                    window.open(response.data.url, '_blank');
                }

                loader.hide();
            });
        },
        downloadEvaluations() {
            let vm = this;
            ProposalService.createEvaluationsPDF({
                region_id: vm.filters.regionId,
                call_id: vm.currentCallId
            }).then(function (response) {
                if (response.data && response.data.success) {
                    window.open(response.data.url, '_blank');
                } else {
                    alert('There are no PDFs uploaded on the selected region');
                }
            });
        },
        changeEvaluationStatus() {
            let vm = this;

            if (confirm('Are you sure you want to change evaluation status?')) {
                let proposal = vm.modalEvaluate.documents;

                ProposalService.updateEvaluationStatus({
                    proposal_id: proposal.id,
                    is_cn: vm.isCN,
                    status: (vm.isCN ? proposal.evaluation_status : proposal.fp_evaluation_status),
                }).then(function (response) {});
            }
        },
        changeFinalStatus() {
            const vm = this;

            if (confirm('Are you sure you want to change final status?')) {
                let documents = vm.modalFinalStatus.documents;

                ProposalService.updateFinalStatus(
                    documents.id, {
                        final_status: documents.final_status
                    }
                )
            }
        },
        async activateTab(ev)
        {
            const vm = this;
            const loader = vm.$loading.show();

            vm.currentCallId = ev.$attrs['data-id'];

            await vm.getFilters();
            await vm.getEvaluationTypes();
            await vm.getProposals();


            loader.hide();
        },

        async getCalls()
        {
            return await CallService.all().then((response) =>
            {
                this.calls = response.data;
                this.currentCallId = this.calls[0].id || 1;
            });
        },

        async getProposals()
        {
            const vm = this;

            await ProposalService.getProposals({
                callId: vm.currentCallId,
                isCn: vm.isCN,
                filters: vm.filters
            }).then(function (response)
            {
                vm.tableData = response.data;
            });
        },

        async getDocuments()
        {
            let vm = this;
            vm.filters.is_cn = vm.isCN;

            vm.getEvaluationTypes();

            return await DocumentService.getDocuments(vm.filters).then(function(response) {
                vm.tableData = response.data;
            })
        },

        async handleFilters()
        {
            const vm = this;
            const loader = vm.$loading.show();


            await vm.getEvaluationTypes();
            await vm.getProposals();
            await vm.getFilters();

            loader.hide();
        },

        async getFilters()
        {
            let vm = this;

            return await ProposalService.getFilters({
                is_cn: vm.isCN,
                filter_application_status: vm.filters.applicationStatus,
                filter_region_id: vm.filters.regionId,
                filter_objectives: vm.filters.objectives,
            }).then((response) =>
            {
                vm.filterOptions = response.data;
            })
        },

        async getEvaluationTypes()
        {
            const vm = this;
            let version = (vm.isCN ? 'concept-note' : 'full-proposal');

            // if (vm.currentCallId === 1)
            // {
            //     version = 'full-proposal';
            // }

            let response = await EvaluationService.getEvaluationTypes(vm.currentCallId, version);

            vm.evaluationTypes = response.data;
        },

        initFuse() {
            this.fuseSearch = new Fuse(this.tableData, {
                keys: this.propsToSearch,
                threshold: 0.3
            });
        },
        exportEvaluation(documents, name) {
            let vm = this;
            let loader = vm.$loading.show();

            EvaluationService.exportEvaluation({
                user_id: documents.id,
                name: name,
                call_id: vm.currentCallId
            }).then((response) => {
                if (response.data && response.data.success) {
                    window.open(response.data.url, '_blank');
                }

                loader.hide();
            });
        },
    },
    async mounted() {
        let vm = this;
        let loader = vm.$loading.show();

        vm.loggedUser = vm.$store.getters['Auth/loggedUser'];

        await vm.getCalls();
        await vm.getEvaluationTypes();
        await vm.getProposals();
        await vm.getFilters();

        vm.initFuse();
        loader.hide();

    },
    watch: {
        /**
         * Searches through the table data by a given query.
         * NOTE: If you have a lot of data, it's recommended to do the search on the Server Side and only display the results here.
         * @param value of the query
         */
        searchQuery(value) {
            let result = this.tableData;
            if (value !== '') {
                result = this.fuseSearch.search(this.searchQuery);
            }
            this.searchedData = result;
        }
    }
};
</script>
<style lang="scss" scoped>
.dropdown-menu li {
    padding: 5px 20px;
    cursor: pointer;
    white-space: nowrap;
}

.dropdown-menu a {
    width: 100%;
}

button {
    white-space: nowrap;
}

.evaluations-list {
    list-style-type: disc !important;
}
</style>
<style>
.el-table .cell {
    overflow: visible !important;
}
.el-select__tags {
    overflow: hidden;
    text-overflow: ellipsis;
}
</style>
